import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { Link, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getComicById, getComicsByCategory, comicCategories } from '@/mocks/comics';
import { getChaptersForComic, getChapterContent } from '@/mocks/chapters';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comicId = parseInt(id || '1', 10);
  const comic = getComicById(comicId);
  const [currentChapter, setCurrentChapter] = useState<number | null>(null);
  const [readingMode, setReadingMode] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setCurrentChapter(null);
    setReadingMode(false);
  }, [comicId]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <div className="px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-4">您要查看的漫画不存在或已下架</p>
            <Link to="/" className="inline-flex items-center gap-1 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-home-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const chapters = getChaptersForComic(comicId);
  const relatedComics = getComicsByCategory(comic.category).filter(c => c.id !== comicId).slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 51漫画在线观看`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2020-01-01',
    dateModified: comic.updatedAt,
    inLanguage: 'zh-CN',
    publisher: { '@type': 'Organization', name: '51漫画', url: 'https://51manhua.com/' },
  };

  if (readingMode && currentChapter) {
    const content = getChapterContent(comicId, currentChapter);
    return (
      <>
        <SEOHead
          title={`${comic.title} 第${currentChapter}话`}
          description={`${comic.title} 第${currentChapter}话 在线免费阅读，51漫画下拉式阅读，流畅阅读体验。${comic.description}`}
          keywords={`${comic.title},51漫画在线观看,第${currentChapter}话,免费漫画,在线阅读,下拉式漫画`}
          canonicalUrl={`https://51manhua.com/comic/${comicId}`}
        />

        <Navbar />

        <main className="pt-20 bg-background-50 min-h-screen">
          <div className="px-4 md:px-6 py-6">
            <div className="max-w-4xl mx-auto">
              {/* Reading Controls */}
              <div className="flex items-center justify-between mb-6 bg-background-100 rounded-xl p-3">
                <button
                  onClick={() => setReadingMode(false)}
                  className="flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i> 返回详情
                </button>
                <h2 className="text-sm font-heading font-bold text-foreground-900">
                  {comic.title} - 第{currentChapter}话
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => currentChapter > 1 && setCurrentChapter(currentChapter - 1)}
                    disabled={currentChapter <= 1}
                    className={`w-8 h-8 flex items-center justify-center rounded-full text-sm transition-colors whitespace-nowrap ${
                      currentChapter <= 1
                        ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
                        : 'bg-primary-100 text-primary-600 hover:bg-primary-200 cursor-pointer'
                    }`}
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  <button
                    onClick={() => currentChapter < chapters.length && setCurrentChapter(currentChapter + 1)}
                    disabled={currentChapter >= chapters.length}
                    className={`w-8 h-8 flex items-center justify-center rounded-full text-sm transition-colors whitespace-nowrap ${
                      currentChapter >= chapters.length
                        ? 'bg-background-200 text-foreground-400 cursor-not-allowed'
                        : 'bg-primary-100 text-primary-600 hover:bg-primary-200 cursor-pointer'
                    }`}
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              </div>

              {/* Chapter Content - Scroll Reading */}
              <article className="bg-background-50 rounded-xl p-6 md:p-10 min-h-[60vh]">
                {content.map((paragraph, idx) => {
                  if (idx === 0) {
                    return (
                      <h1 key={idx} className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6 text-center">
                        {paragraph}
                      </h1>
                    );
                  }
                  if (paragraph === '（本章完）') {
                    return (
                      <p key={idx} className="text-center text-foreground-400 text-sm mt-8 mb-4">{paragraph}</p>
                    );
                  }
                  return (
                    <p key={idx} className="text-sm md:text-base text-foreground-800 leading-loose mb-4">
                      {paragraph}
                    </p>
                  );
                })}

                {/* Scroll hint */}
                <div className="text-center mt-8 py-6 border-t border-background-200/70">
                  <p className="text-xs text-foreground-400 mb-3">
                    <i className="ri-arrow-down-line mr-1"></i> 继续下拉阅读更多内容
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={() => currentChapter > 1 && setCurrentChapter(currentChapter - 1)}
                      disabled={currentChapter <= 1}
                      className={`px-4 py-2 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                        currentChapter <= 1
                          ? 'bg-background-100 text-foreground-400 cursor-not-allowed'
                          : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-500 cursor-pointer'
                      }`}
                    >
                      <i className="ri-arrow-left-line mr-1"></i>上一话
                    </button>
                    <button
                      onClick={() => setReadingMode(false)}
                      className="px-4 py-2 rounded-full text-xs font-medium bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      返回目录
                    </button>
                    <button
                      onClick={() => currentChapter < chapters.length && setCurrentChapter(currentChapter + 1)}
                      disabled={currentChapter >= chapters.length}
                      className={`px-4 py-2 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                        currentChapter >= chapters.length
                          ? 'bg-background-100 text-foreground-400 cursor-not-allowed'
                          : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-500 cursor-pointer'
                      }`}
                    >
                      下一话<i className="ri-arrow-right-line ml-1"></i>
                    </button>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={comic.title}
        description={`${comic.title} - ${comic.description} 51漫画在线观看，免费漫画全集在线阅读、下拉式阅读、实时阅读。`}
        keywords={`${comic.title},51漫画在线观看,${comic.tags?.join(',')},免费漫画,在线阅读`}
        canonicalUrl={`https://51manhua.com/comic/${comicId}`}
        ogType="article"
        schemaObjects={[articleSchema]}
      />

      <Navbar />

      <main className="pt-20">
        <div className="px-4 md:px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '漫画分类', to: '/category/all' },
                { label: comicCategories.find(c => c.slug === comic.category)?.name || comic.category, to: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />

            {/* Comic Info */}
            <div className="flex flex-col md:flex-row gap-6 mt-6">
              <div className="w-full md:w-[240px] flex-shrink-0">
                <div className="aspect-[3/4] rounded-xl overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 51漫画在线观看`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.tags?.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div>
                    <p className="text-xs text-foreground-500">作者</p>
                    <p className="text-sm font-medium text-foreground-900">{comic.author}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">评分</p>
                    <p className="text-sm font-medium text-accent-500"><i className="ri-star-fill text-xs mr-1"></i>{comic.rating}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">人气</p>
                    <p className="text-sm font-medium text-foreground-900">{comic.views}</p>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">总话数</p>
                    <p className="text-sm font-medium text-foreground-900">{comic.chapters}话</p>
                  </div>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">{comic.description}</p>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <button
                    onClick={() => {
                      setCurrentChapter(chapters.length);
                      setReadingMode(true);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    开始阅读 <i className="ri-play-line ml-1"></i>
                  </button>
                  <button
                    onClick={() => {
                      setCurrentChapter(1);
                      setReadingMode(true);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className="px-6 py-3 bg-background-100 text-foreground-700 font-medium rounded-full text-sm hover:bg-primary-50 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    从第一话开始
                  </button>
                </div>
              </div>
            </div>

            {/* Chapter List */}
            <section className="mt-10">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">章节列表</h2>
              <div className="bg-background-100 rounded-xl p-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 max-h-[500px] overflow-y-auto">
                  {chapters.map((chapter) => (
                    <button
                      key={chapter.id}
                      onClick={() => {
                        setCurrentChapter(chapter.id);
                        setReadingMode(true);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="text-left px-3 py-2 rounded-lg hover:bg-primary-50 hover:text-primary-500 transition-colors text-sm text-foreground-700 truncate cursor-pointer"
                    >
                      {chapter.title}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* Related Comics */}
            {relatedComics.length > 0 && (
              <section className="mt-10">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop="">
                  {relatedComics.map((c) => (
                    <Link
                      key={c.id}
                      to={`/comic/${c.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-3">
                        <img src={c.cover} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        <div className="absolute top-2 left-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                            c.status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-secondary-500 text-background-50'
                          }`}>
                            {c.status}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors truncate">{c.title}</h3>
                      <p className="text-xs text-foreground-500 mt-1"><i className="ri-star-fill text-accent-500 text-xs mr-0.5"></i>{c.rating}</p>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}